// Helper functions to manage app settings
// Default settings
export const defaultSettings = {
  timeInterval: 15,
  startHour: 8,
  endHour: 20,
  // in minutes
  defaultView: 'calendar',
  enableContextMenu: true,
  allowRoomBooking: true,
  allowTrialLessons: true,
  maxBookingDays: 60, // in days
  // Labels
  subjectSingularLabel: 'Fach',
  subjectPluralLabel: 'Fächer'
};
// Get settings from localStorage or use defaults
export const getSettings = () => {
  try {
    const savedSettings = localStorage.getItem('sidigo-settings');
    if (savedSettings) {
      return {
        ...defaultSettings,
        ...JSON.parse(savedSettings)
      };
    }
  } catch (error) {
    console.error('Error loading settings:', error);
  }
  return defaultSettings;
};

// Backend Calendar Settings API
import { httpClient, api } from './api';
export type CalendarSettings = {
  startHour: number;
  endHour: number;
  timeInterval: number;
  /** Online-Kurs: Standard-Meeting-Link anbieten (TERM-8.3). */
  onlineCourseEnabled?: boolean;
  defaultMeetingUrl?: string | null;
};
export const getCalendarSettings = async (): Promise<CalendarSettings> => {
  const res = await httpClient<CalendarSettings>('/api/settings/calendar', { method: 'GET', headers: { 'Content-Type': 'application/json' } });
  if (res.error || !res.data) return { startHour: defaultSettings.startHour, endHour: defaultSettings.endHour, timeInterval: defaultSettings.timeInterval };
  return res.data;
};
export const updateCalendarSettings = async (data: CalendarSettings): Promise<CalendarSettings> => {
  const res = await httpClient<CalendarSettings>('/api/settings/calendar', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  if (res.error || !res.data) throw new Error(res.error || 'Kalendereinstellungen konnten nicht gespeichert werden');
  return res.data;
};

// Timezone API
let cachedTimezone: string | null = null;
export const getTimezone = async (): Promise<string> => {
  if (cachedTimezone) return cachedTimezone;
  try {
    const res = await api.get<{ timezone: string }>('/api/settings/timezone');
    cachedTimezone = res.timezone || 'Europe/Berlin';
    return cachedTimezone;
  } catch (error) {
    console.warn('Zeitzone konnte nicht geladen werden, verwende Standard', error);
    return 'Europe/Berlin';
  }
};

export const clearTimezoneCache = () => {
  cachedTimezone = null;
};

/**
 * Fach-Bezeichnungen aus den Einstellungen merken (T-0220).
 *
 * Gelesen wird synchron aus dem Browser-Speicher; gefüllt hat ihn bisher nur die
 * Kalender-Einstellung. Jetzt kommen die Werte mit dem Benutzer (/auth/me) und
 * beim Speichern der Funktionseinstellungen.
 */
export const fachBezeichnungenMerken = (singular?: string | null, plural?: string | null) => {
  try {
    const alt = JSON.parse(localStorage.getItem('sidigo-settings') || '{}');
    localStorage.setItem(
      'sidigo-settings',
      JSON.stringify({
        ...alt,
        subjectSingularLabel: singular || defaultSettings.subjectSingularLabel,
        subjectPluralLabel: plural || defaultSettings.subjectPluralLabel,
      }),
    );
  } catch {
    // Ohne Browser-Speicher gelten die Standardbezeichnungen.
  }
};

/**
 * Kalender & Zeiten aus den Einstellungen merken (ADM-19.1).
 *
 * Terminübersicht und Raumplan lesen Tagesbereich und Raster synchron über
 * `getSettings()`; bisher stand dort nur, was dieser Browser zuletzt selbst
 * gespeichert hatte. Jetzt kommen die Werte mit dem Benutzer (/auth/me).
 */
export type KalenderEinstellungen = {
  startHour?: number;
  endHour?: number;
  timeInterval?: number;
  timezone?: string;
};

export const kalenderEinstellungenMerken = (werte?: KalenderEinstellungen | null) => {
  if (!werte) return;
  try {
    const alt = JSON.parse(localStorage.getItem('sidigo-settings') || '{}');
    const neu = { ...alt };
    if (Number.isFinite(werte.startHour)) neu.startHour = werte.startHour;
    if (Number.isFinite(werte.endHour)) neu.endHour = werte.endHour;
    if (Number.isFinite(werte.timeInterval) && Number(werte.timeInterval) > 0) neu.timeInterval = werte.timeInterval;
    if (werte.timezone) {
      neu.timezone = werte.timezone;
      cachedTimezone = werte.timezone;
    }
    localStorage.setItem('sidigo-settings', JSON.stringify(neu));
  } catch {
    // Ohne Browser-Speicher gelten die Standardwerte.
  }
};

/** Die eingestellte Zeitzone (synchron); Standard Europe/Berlin. */
export const getZeitzone = (): string => {
  const tz = (getSettings() as { timezone?: string }).timezone;
  return typeof tz === 'string' && tz ? tz : 'Europe/Berlin';
};

/**
 * „Heute“ in der Zeitzone der Schule — als lokales Datum um 0 Uhr.
 *
 * Wer das Office aus einer anderen Zeitzone öffnet (Urlaub, Server-Browser in
 * UTC), soll trotzdem den Schultag sehen: kurz nach Mitternacht in Berlin ist in
 * London noch gestern.
 */
export const heute = (jetzt: Date = new Date()): Date => {
  try {
    const teile = new Intl.DateTimeFormat('en-CA', {
      timeZone: getZeitzone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(jetzt);
    const wert = (typ: string) => Number(teile.find((t) => t.type === typ)?.value);
    const jahr = wert('year');
    const monat = wert('month');
    const tag = wert('day');
    if (jahr && monat && tag) return new Date(jahr, monat - 1, tag);
  } catch {
    // Unbekannte Zeitzone: Browserzeit.
  }
  return new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
};

// Convenience helpers
export const getSubjectLabels = () => {
  const settings = getSettings();
  return {
    singular: settings.subjectSingularLabel || defaultSettings.subjectSingularLabel,
    plural: settings.subjectPluralLabel || defaultSettings.subjectPluralLabel,
  };
};
// Get time intervals based on settings
export const getTimeIntervals = () => {
  const { timeInterval, startHour, endHour } = getSettings();
  const parsed = parseInt(timeInterval.toString(), 10);
  const intervalMinutes = Number.isFinite(parsed) && parsed > 0 ? parsed : defaultSettings.timeInterval;
  const sHour = Number.isFinite(Number(startHour)) ? Math.max(0, Math.min(23, Number(startHour))) : defaultSettings.startHour;
  const eHour = Number.isFinite(Number(endHour)) ? Math.max(0, Math.min(23, Number(endHour))) : defaultSettings.endHour;
  // Generate time intervals from settings
  const intervals = [];
  for (let hour = sHour; hour <= eHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const formattedHour = hour.toString().padStart(2, '0');
      const formattedMinute = minute.toString().padStart(2, '0');
      intervals.push(`${formattedHour}:${formattedMinute}`);
    }
  }
  return intervals;
};
// Generate time slots for display
export const generateTimeSlots = () => {
  const intervals = getTimeIntervals();
  return intervals.map(time => {
    const [hour, minute] = time.split(':').map(Number);
    return {
      time,
      hour,
      minute,
      label: time,
      isHourStart: minute === 0
    };
  });
};